import React, { useState, useEffect } from 'react'
import FyMonthlyBarChart from '../../components/Charts/FyMonthlyBarChart'
import { currentFyLabel } from '../../utils/financialYearMonths'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import timesheetsDashboardService from '../../services/timesheetsDashboardService'
import { useModuleView } from '../../hooks/useModuleView'
import ModuleOverviewCard from '../../components/Module/ModuleOverviewCard'
import ModuleViewBackLink from '../../components/Module/ModuleViewBackLink'
import { 
  Users,
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Brain,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  MessageSquare,
  FileText,
  Briefcase,
  Timer,
  UserCheck,
  CalendarDays
} from 'lucide-react'
// Consolidated CSS is imported globally'

const Timesheets = () => {
  const navigate = useNavigate()
  const moduleView = useModuleView()
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const formatHoursTooltip = (hours) => `${Math.round((Number(hours) || 0) * 10) / 10} hrs`

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const response = await timesheetsDashboardService.getDashboardStats()
      if (response.success) {
        setDashboardData(response.data)
      } else {
        if (!response._errorHandled) {
          toast.error(response.message || 'Failed to load dashboard data')
        }
      }
    } catch (error) {
      console.error('Error loading timesheets dashboard data:', error)
      if (!error._errorHandled) {
        if (error.response) {
          toast.error(error.response.data?.message || 'Failed to load dashboard data')
        } else if (!error.request) {
          toast.error('Failed to load dashboard data')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now'
    const date = new Date(timestamp)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)
    
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header" style={{textAlign: 'left'}}>
        <div className="page-title" style={{textAlign: 'left', alignItems: 'flex-start'}}>
          <h1 style={{textAlign: 'left', margin: '0 0 4px 0'}}>Timesheets</h1>
          <p style={{textAlign: 'left', margin: 0}}>Track employee hours and manage time allocation</p>
        </div>
      </div>

      {/* Tab Content */}
      {moduleView === 'overview' && (
        <div className="tab-content">
          <ModuleViewBackLink onClick={() => navigate('/timesheets')} />

          {/* Key Metrics Grid */}
          <div className="dashboard-metrics-grid">
            {/* Timesheet Metrics */}
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <Timer size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.hoursLogged?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.hoursLogged?.change >= 0 ? '+' : ''}{Math.round(dashboardData?.hoursLogged?.change || 0)}h</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.hoursLogged?.hours?.toLocaleString() || '0')}
              </div>
              <div className="metric-title">Hours Logged</div>
              <div className="metric-subtitle">This month</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon">
                  <UserCheck size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.activeUsers?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.activeUsers?.change >= 0 ? '+' : ''}{dashboardData?.activeUsers?.change || 0}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.activeUsers?.count || 0)}
              </div>
              <div className="metric-title">Active Users</div>
              <div className="metric-subtitle">Team members</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon">
                  <AlertTriangle size={16} />
                </div>
                <div className="metric-trend negative">
                  <TrendingDown size={12} />
                  <span>{dashboardData?.pendingApproval?.status || 'AWAITING REVIEW'}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.pendingApproval?.count || 0)}
              </div>
              <div className="metric-title">Pending Approval</div>
              <div className="metric-subtitle">Timesheets</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon">
                  <CalendarDays size={16} />
                </div>
                <div className="metric-trend neutral">
                  <CheckCircle size={12} />
                  <span>{dashboardData?.submissionRate?.status || 'ON TIME'}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : `${dashboardData?.submissionRate?.rate || 0}%`}
              </div>
              <div className="metric-title">Submission Rate</div>
              <div className="metric-subtitle">Weekly submissions</div>
            </div>
          </div>

          {/* Action Items & Alerts */}
          <div className="dashboard-action-section">
            <div className="dashboard-action-items">
              <div className="section-header">
                <h3>Timesheet Action Items</h3>
                <span className="item-count">{dashboardData?.actionItems?.length || 0} items</span>
              </div>
              <div className="action-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading action items...</div>
                ) : dashboardData?.actionItems && dashboardData.actionItems.length > 0 ? (
                  dashboardData.actionItems.map((item, index) => {
                    const iconMap = {
                      approval: AlertTriangle,
                      overtime: Clock,
                      project: Calendar
                    }
                    const Icon = iconMap[item.type] || AlertTriangle
                    return (
                      <div key={index} className={`action-item ${item.priority}`}>
                        <div className="action-icon">
                          <Icon size={16} />
                        </div>
                        <div className="action-content">
                          <div className="action-title">{item.title}</div>
                          <div className="action-description">{item.description}</div>
                        </div>
                        <div className="action-time">{formatTimeAgo(item.timestamp)}</div>
                      </div>
                    )
                  })
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No action items</div>
                )}
              </div>
            </div>

            <div className="dashboard-team-activity">
              <div className="section-header">
                <h3>Timesheet Team Activity</h3>
                <span className="activity-count">{dashboardData?.teamActivity?.length || 0} updates</span>
              </div>
              <div className="activity-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading team activity...</div>
                ) : dashboardData?.teamActivity && dashboardData.teamActivity.length > 0 ? (
                  dashboardData.teamActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-avatar">{activity.employeeInitials || 'UN'}</div>
                      <div className="activity-content">
                        <div className="activity-text">
                          <strong>{activity.employeeName || 'Unknown'}</strong> {activity.activity}
                        </div>
                        <div className="activity-time">{formatTimeAgo(activity.timestamp)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No recent activity</div>
                )}
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="dashboard-charts-section">
            <div className="dashboard-chart-card revenue-chart">
              <div className="chart-header">
                <h3>Hours Logged Trend</h3>
                <p>{dashboardData?.financialYear ? `FY ${dashboardData.financialYear}` : `FY ${currentFyLabel()}`}</p>
              </div>
              <div className="chart-content">
                <FyMonthlyBarChart
                  series={dashboardData?.hoursLoggedTrend}
                  valueKey="revenue"
                  formatValue={(v) => `${Math.round((Number(v) || 0) * 10) / 10}h`}
                  formatTooltip={formatHoursTooltip}
                  emptyMessage="No hours logged this financial year"
                />
              </div>
            </div>

            <div className="dashboard-chart-card customer-health">
              <div className="chart-header">
                <h3>Timesheet Status Distribution</h3>
                <p>Current week</p>
              </div>
              <div className="chart-content">
                <div className="health-distribution">
                  {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading...</div>
                  ) : dashboardData?.statusDistribution?.length > 0 ? (
                    dashboardData.statusDistribution.map((row, index) => {
                      const classes = ['excellent', 'good', 'at-risk', 'churned']
                      const cls = classes[index % classes.length]
                      return (
                        <div key={row.name || index} className={`health-segment ${cls}`}>
                          <div className="segment-bar" style={{ width: `${Math.max(row.percent || 0, row.count > 0 ? 4 : 0)}%` }} />
                          <div className="segment-info">
                            <span className="segment-label">{row.name}</span>
                            <span className="segment-count">{row.count ?? 0}</span>
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                      No weekly timesheets this week
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'masters' && (
        <div className="tab-content">
          <div className="timesheets-masters-content">
            <div className="timesheets-masters-header">
              <h2>Timesheet Masters</h2>
              <p>Manage your timesheet master data and configurations</p>
            </div>
            <div className="timesheets-masters-grid">
              <div className="timesheets-master-card">
                <div className="timesheets-master-icon">
                  <Users size={24} />
                </div>
                <div className="timesheets-master-content">
                  <h3>Employees</h3>
                  <p>Employee profiles and timesheet access</p>
                  <div className="timesheets-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.employees ?? 0} Employees`}
                  </div>
                </div>
              </div>
              <div className="timesheets-master-card">
                <div className="timesheets-master-icon">
                  <Briefcase size={24} />
                </div>
                <div className="timesheets-master-content">
                  <h3>Projects</h3>
                  <p>Project codes and time allocation</p>
                  <div className="timesheets-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.projects ?? 0} Projects`}
                  </div>
                </div>
              </div>
              <div className="timesheets-master-card">
                <div className="timesheets-master-icon">
                  <Target size={24} />
                </div>
                <div className="timesheets-master-content">
                  <h3>Activities</h3>
                  <p>Activity types and task categories</p>
                  <div className="timesheets-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.activities ?? 0} Activities`}
                  </div>
                </div>
              </div>
              <div className="timesheets-master-card">
                <div className="timesheets-master-icon">
                  <UserCheck size={24} />
                </div>
                <div className="timesheets-master-content">
                  <h3>Approvers</h3>
                  <p>Timesheet approval hierarchy and workflows</p>
                  <div className="timesheets-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.approvers ?? 0} Approvers`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'transactions' && (
        <div className="tab-content">
          <div className="timesheets-transactions-content">
            <div className="timesheets-transactions-header">
              <h2>Timesheet Transactions</h2>
              <p>Track and manage all timesheet entries and approvals</p>
            </div>
            <div className="timesheets-transactions-grid">
              <ModuleOverviewCard
                prefix="timesheets-transaction"
                moduleName="Timesheets"
                showArrow={false}
                onClick={() => navigate('/timesheets?view=overview')}
              />
              <div className="timesheets-transaction-card clickable" onClick={() => navigate('/timesheets/list')} style={{ cursor: 'pointer' }}>
                <div className="timesheets-transaction-icon">
                  <FileText size={24} />
                </div>
                <div className="timesheets-transaction-content">
                  <h3>Timesheet List</h3>
                  <p>View and manage all timesheet entries</p>
                  <div className="timesheets-transaction-count">View All</div>
                </div>
              </div>
              <div className="timesheets-transaction-card clickable" onClick={() => navigate('/timesheets/time-entry')}>
                <div className="timesheets-transaction-icon">
                  <Timer size={24} />
                </div>
                <div className="timesheets-transaction-content">
                  <h3>Time Entries</h3>
                  <p>Daily time logging and project tracking</p>
                  <div className="timesheets-transaction-count">
                    {loading ? '…' : `${(dashboardData?.transactionCounts?.timeEntries ?? 0).toLocaleString('en-IN')} Entries`}
                  </div>
                </div>
              </div>
              <div className="timesheets-transaction-card clickable" onClick={() => navigate('/timesheets/weekly-timesheet')}>
                <div className="timesheets-transaction-icon">
                  <Calendar size={24} />
                </div>
                <div className="timesheets-transaction-content">
                  <h3>Weekly Timesheets</h3>
                  <p>Weekly timesheet submissions and reviews</p>
                  <div className="timesheets-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.weeklySubmitted ?? 0} Submitted`}
                  </div>
                </div>
              </div>
              <div className="timesheets-transaction-card clickable" onClick={() => navigate('/timesheets/timesheet-approval')}>
                <div className="timesheets-transaction-icon">
                  <UserCheck size={24} />
                </div>
                <div className="timesheets-transaction-content">
                  <h3>Approvals</h3>
                  <p>Timesheet approval and rejection workflows</p>
                  <div className="timesheets-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.weeklyApproved ?? 0} Approved`}
                  </div>
                </div>
              </div>
              <div className="timesheets-transaction-card clickable" onClick={() => navigate('/timesheets/overtime')}>
                <div className="timesheets-transaction-icon">
                  <Timer size={24} />
                </div>
                <div className="timesheets-transaction-content">
                  <h3>Overtime</h3>
                  <p>Overtime hours and compensation tracking</p>
                  <div className="timesheets-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.overtime ?? 0} Overtime`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'reports' && (
        <div className="tab-content">
          <div className="timesheets-reports-content">
            <div className="timesheets-reports-header">
              <h2>Timesheet Reports</h2>
              <p>Comprehensive timesheet analytics and reporting</p>
            </div>
            <div className="timesheets-reports-grid">
              <div className="timesheets-report-card">
                <div className="timesheets-report-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="timesheets-report-content">
                  <h3>Time Analysis</h3>
                  <p>Time utilization and productivity analysis</p>
                  <div className="timesheets-report-status">Updated Today</div>
                </div>
              </div>
              <div className="timesheets-report-card">
                <div className="timesheets-report-icon">
                  <Users size={24} />
                </div>
                <div className="timesheets-report-content">
                  <h3>Employee Reports</h3>
                  <p>Individual employee timesheet and performance</p>
                  <div className="timesheets-report-status">Updated Today</div>
                </div>
              </div>
              <div className="timesheets-report-card">
                <div className="timesheets-report-icon">
                  <Briefcase size={24} />
                </div>
                <div className="timesheets-report-content">
                  <h3>Project Reports</h3>
                  <p>Project time allocation and cost analysis</p>
                  <div className="timesheets-report-status">Updated Today</div>
                </div>
              </div>
              <div className="timesheets-report-card">
                <div className="timesheets-report-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="timesheets-report-content">
                  <h3>Billing Reports</h3>
                  <p>Client billing and invoice generation</p>
                  <div className="timesheets-report-status">Updated Today</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Timesheets




